// Based on Vssf/WebPlatform/Features/VSSPreview/Scripts/SignalR/Hubs.ts
/// <amd-module name="VSS/Features/SignalR/Hub" />
import { Connection } from "signalR";
import { jqueryShimmed as $ } from "VSS/Features/SignalR/3rdParty/jquery.shimmed";
import { getRandomNumberBetweenRange, isChrome } from "VSS/Features/SignalR/Common";
import { disposeProxies, getBaseProxyObject, getHubProxies, registerProxy } from "VSS/Features/SignalR/HubProxies";
import { ISignalRHubProxy } from "VSS/Features/SignalR/HubProxies.types";
import { IAuthorizationTokenProvider, IVssPageContext } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { ITokenProviderService } from "VSS/Platform/TokenProvider";
import { isSameOrigin } from "VSS/Platform/Util/Url";

namespace Constants {
    export const MinHardReconnectDelayMS: number = 5 * 1000; // 5 seconds
    export const MaxHardReconnectDelayMS: number = 60 * 1000; // 1 minute
    export const MaxHardReconnectAttempts = 10;
    export const UserInactiveTimeoutMS: number = 4 * 60 * 60 * 1000; // 4 hours
    export const MaxReconnectingStateDelayMS: number = 60 * 1000; // 1 minute
}

export interface IHubListener {
    isConnecting(): void;
    isConnected(): void;
    isDisconnected(): void;
    isReconnected(): void;
    isReconnecting(): void;
    isSlow(): void;

    isUserActive(): void;
    isUserInactive(): void;
}

export interface IProxyRegistration<C, S> {
    getProxyToRegister: getProxyToRegisterType<C, S>;
    registerProxyClient: RegisterProxyClientType<C, S>;
}

export interface IHubHostInformation {
    hostId: string;
    isHosted: boolean;
    hostPath: string;
    projectId?: string;
}

export type getProxyToRegisterType<C, S> = (baseProxy: ISignalRHubProxy<C, S>) => ISignalRHubProxy<C, S>;
export type RegisterProxyClientType<C, S> = (proxy: ISignalRHubProxy<C, S>) => void;

export interface ISingleConnectionSignalRHubOptions<C, S> extends ISignalRHubOptions<C, S> {
    proxyToRegister: getProxyToRegisterType<C, S>;
}

export class SingleConnectionSignalRHub<C, S> {
    private static s_hub: SignalRHub<any, any> | null;
    private static s_registeredHubs: string[] = [];
    private static s_initialized = false;
    private static s_proxiesToRegister: { [key: string]: IProxyRegistration<any, any> } = {};

    constructor(options: ISingleConnectionSignalRHubOptions<C, S>) {
        if (!SingleConnectionSignalRHub.s_hub) {
            SingleConnectionSignalRHub.s_hub = new SignalRHub<C, S>(options);
            SingleConnectionSignalRHub.s_registeredHubs.push(options.name);
        }

        SingleConnectionSignalRHub.s_proxiesToRegister[options.name] = {
            getProxyToRegister: options.proxyToRegister
        } as IProxyRegistration<C, S>;
    }

    protected getHub(hubName: string): ISignalRHubProxy<C, S> | null {
        return SingleConnectionSignalRHub.s_hub && SingleConnectionSignalRHub.s_hub.getHub(hubName);
    }

    protected initializeProxyClient(hubName: string, registerProxyClient: RegisterProxyClientType<C, S>, listener?: IHubListener) {
        SingleConnectionSignalRHub.s_proxiesToRegister[hubName].registerProxyClient = registerProxyClient;
        if (listener && SingleConnectionSignalRHub.s_hub) {
            SingleConnectionSignalRHub.s_hub.addListener(listener);
        }
    }

    protected start(): Promise<void> {
        if (SingleConnectionSignalRHub.s_hub) {
            this._ensureHubInitialization();
            return SingleConnectionSignalRHub.s_hub.start();
        } else {
            return Promise.reject("hub is not initialized");
        }
    }

    public stop(stopHardReconnectTrials?: boolean): void {
        if (SingleConnectionSignalRHub.s_hub) {
            SingleConnectionSignalRHub.s_hub.stop(stopHardReconnectTrials);
        }
    }

    protected dispose() {
        if (SingleConnectionSignalRHub.s_hub) {
            SingleConnectionSignalRHub.s_hub.dispose();
            SingleConnectionSignalRHub.s_hub = null;
            SingleConnectionSignalRHub.s_registeredHubs = [];
            SingleConnectionSignalRHub.s_initialized = false;
            SingleConnectionSignalRHub.s_proxiesToRegister = {};
        }
    }

    private _ensureHubInitialization() {
        if (SingleConnectionSignalRHub.s_hub && !SingleConnectionSignalRHub.s_initialized) {
            Object.keys(SingleConnectionSignalRHub.s_proxiesToRegister).forEach(hub => {
                if (!SingleConnectionSignalRHub.s_proxiesToRegister[hub].registerProxyClient) {
                    console.warn("Hub " + hub + "has no client registered, no one would be listening to events from this hub.");
                }
            });
            SingleConnectionSignalRHub.s_hub.initialize(SingleConnectionSignalRHub.s_proxiesToRegister);
            SingleConnectionSignalRHub.s_initialized = true;
        }
    }
}

export interface ISignalRHubOptions<C, S> {
    name: string;
    hostInformation: IHubHostInformation;
    listener?: IHubListener;
    userInactivityTimeoutMs?: number;
    pageContext?: IVssPageContext;
    /** Remove `_apis` in the url - defaults to falsy */
    removeApiPath?: boolean;
    /** Allow passing in a default value for LWP consumers */
    useSignalRAppPoolDefaultValue?: boolean;
    /** Allow passing in a token provider for non-TFS services */
    tokenProvider?: IAuthorizationTokenProvider;
}

export class SignalRHub<C, S> {
    private _hubName: string;

    private _listeners: Array<IHubListener>;
    private _hubConnection: HubConnection<C, S>;
    private _intialized: boolean = false;
    private _startConnectionPromise: Promise<void> | undefined;
    private static _featureFlagSignalRSeparateAppPool = "WebAccess.SignalR.AppPool";

    constructor(options: ISignalRHubOptions<C, S>) {
        this._hubName = options.name;
        this._listeners = [];
        if (options.listener) {
            this._listeners.push(options.listener);
        }

        let connectionUrl = options.removeApiPath ? options.hostInformation.hostPath : `${options.hostInformation.hostPath}_apis`;
        if (options.hostInformation.projectId) {
            connectionUrl += `/${options.hostInformation.projectId}`;
        }

        if (
            options.hostInformation.isHosted &&
            options.pageContext &&
            SignalRHub.signalRAppPoolFeatureEnabled(options.pageContext, options.useSignalRAppPoolDefaultValue) &&
            !isAbsoluteUrl(connectionUrl)
        ) {
            connectionUrl = "/_signalr" + connectionUrl;
        }

        if (connectionUrl === "/") {
            // For *.visualstudio.com urls, we will just have "/" as the host path
            // We want to remove this
            connectionUrl = "";
        }

        // if no token provider was supplied, see if we can get an MSAL one
        let tokenProvider = options.tokenProvider;
        if (!tokenProvider && options.pageContext) {
            tokenProvider = options.pageContext.getService<ITokenProviderService>("ITokenProviderService").getSilentProvider();
        }

        this._hubConnection = new HubConnection({
            hubName: options.name,
            listeners: this._listeners,
            connectionUrl: connectionUrl,
            hostInformation: options.hostInformation,
            userInactivityTimeoutMs: options.userInactivityTimeoutMs,
            tokenProvider: tokenProvider
        });
    }

    private static signalRAppPoolFeatureEnabled(pageContext: IVssPageContext, defaultValue?: boolean): boolean {
        return isFeatureFlagEnabled(pageContext, SignalRHub._featureFlagSignalRSeparateAppPool, defaultValue || false);
    }

    public initializeProxy(proxyToRegister: getProxyToRegisterType<C, S>, proxyClient: RegisterProxyClientType<C, S>) {
        const proxiesToRegister: { [key: string]: IProxyRegistration<C, S> } = {};
        proxiesToRegister[this._hubName] = {
            getProxyToRegister: proxyToRegister,
            registerProxyClient: proxyClient
        };
        this.initialize(proxiesToRegister);
    }

    public initialize(proxiesToRegister: { [key: string]: IProxyRegistration<C, S> }) {
        this._hubConnection.initialize(proxiesToRegister);
        this._intialized = true;
    }

    public get hub(): ISignalRHubProxy<C, S> | null {
        return this.getHub(this._hubName);
    }

    public addListener(listener: IHubListener) {
        this._listeners.push(listener);
    }

    public getHubName() {
        return this._hubName;
    }

    public getHub(hubName?: string): ISignalRHubProxy<C, S> | null {
        if (!this._intialized) {
            console.error("SignalR: Not intialized, hub can't exist");
            return null;
        }

        return this._hubConnection.getHub(hubName || this._hubName);
    }

    public start(): Promise<void> {
        if (!this._intialized) {
            return Promise.reject("Hub isn't initialized, call initialize method");
        }

        if (!this._startConnectionPromise) {
            this._startConnectionPromise = this._hubConnection.start();
        }

        return this._startConnectionPromise;
    }

    public stop(stopHardReconnectTrials?: boolean) {
        if (!this._startConnectionPromise) {
            console.error("SignalR: Not started, exiting stop");
            return;
        }

        this._hubConnection.stop(stopHardReconnectTrials);
        this._startConnectionPromise = undefined;
    }

    public registerProxies(proxiesToRegister: { [key: string]: IProxyRegistration<C, S> }) {
        this._hubConnection.registerProxies(proxiesToRegister);
    }

    public dispose() {
        this._listeners = [];
        this.stop();
        this._intialized = false;
        disposeProxies();
    }
}

interface IHubConnectionOptions<C, S> {
    hubName: string;
    connectionUrl: string;
    hostInformation: IHubHostInformation;
    listeners?: Array<IHubListener>;
    userInactivityTimeoutMs?: number;
    tokenProvider?: IAuthorizationTokenProvider;
}

class HubConnection<C, S> {
    private _connection: Connection | null = null;
    private _listeners: Array<IHubListener>;
    private _hubs: { [key: string]: ISignalRHubProxy<C, S> } = {};
    private _hostInformation: IHubHostInformation;
    private _connectionUrl: string;
    private _initializing: boolean = false;

    private _tokenProvider: IAuthorizationTokenProvider | undefined;

    private _pendingProxies: { [key: string]: IProxyRegistration<C, S> } = {};

    private _startPromise: Promise<void> | undefined;
    private _connected: boolean = false;

    private _hardReconnectTimeout: number | undefined;
    private _hardReconnectAttempts: number = 0;

    private _userInactiveTimeout: number | undefined;
    private _userInactivityMessageInvoked: boolean = false;

    private _shouldTryHardReconnect: boolean = true;
    private _isConnectedOnce: boolean = false;

    private _userInactivityTimeoutMs: number;

    constructor(options: IHubConnectionOptions<C, S>) {
        this._listeners = options.listeners || [];
        this._hostInformation = options.hostInformation;
        this._connectionUrl = options.connectionUrl;
        this._tokenProvider = options.tokenProvider;

        this._userInactivityTimeoutMs = options.userInactivityTimeoutMs || Constants.UserInactiveTimeoutMS;
    }

    public getHub(name: string) {
        return this._hubs[name];
    }

    public initialize(proxiesToRegister: { [key: string]: IProxyRegistration<C, S> }) {
        if (!this._initializing) {
            this._initializeConnection(this._connectionUrl, proxiesToRegister);
        }
    }

    public start(): Promise<void> {
        if (!this._connection) {
            return Promise.reject("Hubconnection is not initialized, call initialize method");
        }
        if (!this._startPromise) {
            const options: SignalR.ConnectionOptions = {};

            // typed definition files thinks it's jquery promise, but because we use shim, we know we get ES6 promise
            this._startPromise = this._connection.start(options) as any;
        }

        return (this._startPromise as Promise<void>)
            .then(() => {
                console.info("SignalR: connected!");
                this._cancelHardReconnectAttempt();
            })
            .catch(e => {
                if (this._isConnectedOnce) {
                    // not throwing here since we will be reconnecting
                    console.warn("SignalR: Cannot start connection...hard reconnect attempt " + this._hardReconnectAttempts);
                } else {
                    // connection never started!
                    return Promise.reject("SignalR: Connection did not start.." + e.toString());
                }
            });
    }

    public stop(stopHardReconnectTrials?: boolean) {
        if (stopHardReconnectTrials) {
            this._shouldTryHardReconnect = false;
        }

        if (this._connection) {
            console.info("SignalR: stopping...");
            this._connection.stop(true);
        } else {
            console.error("SignalR: Connection is not initialized, nothing to stop");
        }

        this._startPromise = undefined;
        this._connected = false;
        this._cancelHardReconnectAttempt();
    }

    public registerProxies(proxiesToRegister: { [key: string]: IProxyRegistration<C, S> }) {
        if (this._connection) {
            this._registerProxies(proxiesToRegister);
        } else {
            Object.keys(proxiesToRegister).forEach(hubName => {
                const options = proxiesToRegister[hubName];
                this._pendingProxies[hubName] = {
                    getProxyToRegister: options.getProxyToRegister,
                    registerProxyClient: options.registerProxyClient
                };
            });
        }
    }

    public dispose() {
        this._listeners = [];
        this._pendingProxies = {};
        this._isConnectedOnce = false;
        this.stop();
        document.removeEventListener("visibilitychange", this._handleUserActiveStateChange, false);
    }

    private _initializeConnection(connectionUrl: string, proxiesToRegister: { [key: string]: IProxyRegistration<C, S> }) {
        if (this._connection) {
            return;
        }

        this._shouldTryHardReconnect = true;
        this._initializing = true;
        if (!$.hubConnection) {
            console.error("SignalR: Connection can't be initialized, signalR is not loaded.");
            return;
        }

        const options: SignalR.Hub.Options = {
            logging: false,
            useDefaultPath: true,
            qs: `contextToken=${this._hostInformation.hostId}`
        };
        this._connection = $.hubConnection(connectionUrl, options);

        // the SignalRLogic is a singleton, so when we override its .ajax() method, we can't supply a token provider directly
        // instead, we attach the token provider to the connection, which is passed as a parameter to .ajax()
        (<any>this._connection).tokenProvider = this._tokenProvider;

        if (this._connection) {
            this._overrideSignalRGetUrl($.signalR.transports._logic);
            this._overrideSignalRAjax($.signalR.transports._logic);

            this._connection.connectionSlow(() => {
                console.info("SignalR: slow connection from " + connectionUrl);
                this._listeners.forEach(listener => listener.isSlow());
            });

            this._connection.reconnected(() => {
                console.info("SignalR: reconnected to " + connectionUrl);
                this._listeners.forEach(listener => listener.isReconnected());
            });

            this._connection.reconnecting(() => {
                console.info("SignalR: reconnecting to " + connectionUrl);
                this._listeners.forEach(listener => listener.isReconnecting());
            });

            this._connection.disconnected(() => {
                console.info("SignalR: disconnected from " + connectionUrl);
                this._listeners.forEach(listener => listener.isDisconnected());
                this._tryHardReconnect();
            });

            this._connection.stateChanged(change => {
                const newState = change.newState;
                const oldState = change.oldState;
                if (oldState === SignalR.ConnectionState.Disconnected && newState === SignalR.ConnectionState.Connecting) {
                    console.info("SignalR: moved from disconnected to connecting for " + connectionUrl);
                    this._listeners.forEach(listener => listener.isConnecting());
                }
                if (oldState === SignalR.ConnectionState.Connecting && newState === SignalR.ConnectionState.Connected) {
                    console.info("SignalR: moved from connecting to connected for " + connectionUrl);
                    this._listeners.forEach(listener => listener.isConnected());
                    this._connected = true;
                    this._isConnectedOnce = true;
                }
            });

            // Turn off logging by default
            (this._connection as any).logging = false;
        }

        this.registerProxies(proxiesToRegister);

        this._initializing = false;

        // Set up handling the visibility change event - tabs that are hidden will be considered inactive
        // and inheriting classes will be notified of inactivity
        document.addEventListener("visibilitychange", this._handleUserActiveStateChange, false);
    }

    private _cancelHardReconnectAttempt() {
        if (this._hardReconnectTimeout) {
            clearTimeout(this._hardReconnectTimeout);
        }

        this._hardReconnectTimeout = undefined;
    }

    private _registerProxies(proxiesToRegister: { [key: string]: IProxyRegistration<C, S> }) {
        Object.keys(proxiesToRegister).forEach(hubName => {
            const options = proxiesToRegister[hubName];
            this._registerProxy(hubName, options.getProxyToRegister, options.registerProxyClient);
        });

        Object.keys(this._pendingProxies).forEach(pendingHubName => {
            const options = this._pendingProxies[pendingHubName];
            this._registerProxy(pendingHubName, options.getProxyToRegister, options.registerProxyClient);
        });

        this._pendingProxies = {};

        this._hubs = getHubProxies();
    }

    private _registerProxy(hubName: string, getProxyToRegister: getProxyToRegisterType<C, S>, registerProxyClient: RegisterProxyClientType<C, S>) {
        const connection = this._connection as Connection;
        // Let's register proxies now
        const proxyObject = getBaseProxyObject<C, S>(connection, hubName);
        const proxyToRegister = getProxyToRegister(proxyObject);
        registerProxyClient(proxyToRegister);
        registerProxy(connection, hubName, proxyToRegister);
    }

    private _tryHardReconnect() {
        if (!this._isConnectedOnce || !this._shouldTryHardReconnect) {
            return;
        }

        this._startPromise = undefined;
        this._connected = false;
        if (!this._hardReconnectTimeout && this._hardReconnectAttempts < Constants.MaxHardReconnectAttempts) {
            this._hardReconnectAttempts++;
            let delay = getRandomNumberBetweenRange(Constants.MinHardReconnectDelayMS, Constants.MaxHardReconnectDelayMS);
            if (this._hardReconnectAttempts >= 2 && this._hardReconnectAttempts < 5) {
                // Add a minute to the delay
                delay = delay + 60 * 1000;
            } else if (this._hardReconnectAttempts >= 5 && this._hardReconnectAttempts < 7) {
                // Add 3 minutes to delay
                delay = delay + 3 * 60 * 1000;
            } else if (this._hardReconnectAttempts >= 7 && this._hardReconnectAttempts < 8) {
                // Add 5 minutes to delay
                delay = delay + 5 * 60 * 1000;
            } else if (this._hardReconnectAttempts >= 8) {
                // Add 15 minutes to delay
                delay = delay + 15 * 60 * 1000;
            }

            this._hardReconnectTimeout = setTimeout(() => {
                this.start();
            }, delay);
        }
    }

    private _handleUserActiveStateChange = () => {
        document.visibilityState == "hidden" ? this._onUserInactive() : this._onUserActive();
    };

    private _onUserInactive() {
        if (!this._userInactiveTimeout) {
            this._userInactiveTimeout = setTimeout(() => {
                // Let's call it only once for inactivity
                if (this._connected && !this._userInactivityMessageInvoked) {
                    this._listeners.forEach(listener => listener.isUserInactive());
                    this._userInactivityMessageInvoked = true;
                }
            }, this._userInactivityTimeoutMs);
        }
    }

    private _onUserActive() {
        if (this._userInactiveTimeout) {
            clearTimeout(this._userInactiveTimeout);
        }

        this._listeners.forEach(listener => listener.isUserActive());
        this._userInactiveTimeout = undefined;
        this._userInactivityMessageInvoked = false;
    }

    private _overrideSignalRGetUrl(signalRLogic: any) {
        if (signalRLogic && !signalRLogic.vstsURLOverridden) {
            // override signalR's getUrl in order to support https://codedev.ms/{accountName}
            // see https://github.com/SignalR/SignalR/issues/3776 for more information
            const getUrl = signalRLogic.getUrl;

            signalRLogic.getUrl = (connection: any, transport: any, reconnecting: any, poll: any, ajaxPost: any) => {
                const url = getUrl(connection, transport, reconnecting, poll, ajaxPost);

                // for handling urls for services such as RM, which uses absolute connectionUrl
                let startIndex = 0;
                if (transport === "webSockets") {
                    const prefix = connection.protocol + "//" + connection.host;
                    startIndex = connection.url.indexOf(prefix) >= 0 ? connection.url.indexOf(prefix) + prefix.length : 0;
                }

                return (
                    connection.url.substring(startIndex) + url.substring(url.indexOf(connection.appRelativeUrl) + connection.appRelativeUrl.length)
                );
            };

            signalRLogic.vstsURLOverridden = true;
        }
    }

    private _overrideSignalRAjax(signalRLogic: any) {
        if (signalRLogic && !signalRLogic.vstsAjaxOverridden) {
            const originalAjax = signalRLogic.ajax;

            signalRLogic.ajax = function (connection: any, options: any) {
                // if we don't have a token provider, use the default behavior
                if (!connection.tokenProvider) {
                    return originalAjax(connection, options);
                }

                // jquery signalr will automatically set connection.withCredentials to true if the request is cross-origin
                // since we also have a token provider, we need to set withCredentials to false. otherwise CORS will block the request with
                // "The value of the 'Access-Control-Allow-Origin' header in the response must not be the wildcard '*' when the request's credentials mode is 'include'"
                // and withCredentials is not necessary if we have a token provider
                let withCredentials = connection.withCredentials;
                if (connection.url && isCrossOrigin(connection.url)) {
                    withCredentials = false;
                }

                return $.ajax(Object.assign(Object.assign(Object.assign({}, $.signalR.ajaxDefaults), {
                    type: "GET",
                    data: {},
                    xhrFields: { withCredentials: withCredentials },
                    contentType: connection.contentType,
                    dataType: connection.ajaxDataType,
                    beforeSend: (xhr: XMLHttpRequest) => {
                        return connection.tokenProvider.getAuthorizationHeader()
                            .then((authorizationHeader: string) => {
                                xhr.setRequestHeader("Authorization", authorizationHeader);
                            });
                    }
                }), options));
            };

            signalRLogic.vstsAjaxOverridden = true;
        }
    }
}

function isAbsoluteUrl(url: string): boolean {
    return url.indexOf("http:") === 0 || url.indexOf("https:") === 0 || url.indexOf("//") === 0;
}

function isCrossOrigin(url: string): boolean {
    url = url.toLowerCase();
    return isAbsoluteUrl(url) && !isSameOrigin(url, window.location.href);
}
